import React, { useState, useEffect, useRef } from 'react';
import TopBar from '../components/layout/TopBar';
import api from '../utils/api';
import toast from 'react-hot-toast';

const QUICK_ITEMS = ['Fly spray','Fly mask','Wormer','Saddle pad','Breeches','Hoof pick','Liniment','Show sheen','Polo wraps','Hay net','Electrolytes','Fly sheet'];

const ShoppingPage = () => {
  const [tab, setTab] = useState('list');
  const [list, setList] = useState(null);
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ product_name: '', source: 'manual', quantity: 1, notes: '' });
  const searchTimeout = useRef(null);

  const loadList = async () => {
    try {
      const { data } = await api.get('/shopping/list');
      setList(data);
    } catch { toast.error('Failed to load list'); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadList(); }, []);

  const doSearch = async (q) => {
    if (!q.trim()) { setSearchResults(null); return; }
    setSearching(true);
    try {
      const [storeRes, catRes] = await Promise.allSettled([
        api.get(`/shopping/search?q=${encodeURIComponent(q)}`),
        api.get(`/supplies/search?q=${encodeURIComponent(q)}`),
      ]);
      const data = storeRes.status === 'fulfilled' ? storeRes.value.data : { cr_results: [], query: q };
      let supplyResults = [];
      if (catRes.status === 'fulfilled') {
        const d = catRes.value.data;
        supplyResults = Array.isArray(d) ? d : (d.results || d.catalog || d.items || []);
      }
      setSearchResults({ ...data, supply_results: supplyResults });
    } catch { setSearchResults({ cr_results: [], supply_results: [], query: q }); }
    finally { setSearching(false); }
  };

  const handleSearchInput = (val) => {
    setSearchQ(val);
    clearTimeout(searchTimeout.current);
    if (val.length > 1) {
      searchTimeout.current = setTimeout(() => doSearch(val), 400);
    } else {
      setSearchResults(null);
    }
  };

  const addToList = async (item) => {
    try {
      const { data } = await api.post('/shopping/list/items', item);
      setList(prev => ({ ...prev, items: [...(prev?.items || []), data] }));
      toast.success(`${item.product_name} added!`);
      setSearchQ('');
      setSearchResults(null);
      setShowAdd(false);
    } catch { toast.error('Failed to add item'); }
  };

  const handleAddManual = () => {
    if (!addForm.product_name.trim()) return toast.error('Enter a product name');
    addToList(addForm);
    setAddForm({ product_name: '', source: 'manual', quantity: 1, notes: '' });
  };

  const toggleCheck = async (item) => {
    try {
      await api.put(`/shopping/list/items/${item.id}`, { is_checked: !item.is_checked });
      setList(prev => ({ ...prev, items: prev.items.map(i => i.id === item.id ? { ...i, is_checked: !i.is_checked } : i) }));
    } catch { toast.error('Failed'); }
  };

  const removeItem = async (id) => {
    try {
      await api.delete(`/shopping/list/items/${id}`);
      setList(prev => ({ ...prev, items: prev.items.filter(i => i.id !== id) }));
    } catch { toast.error('Failed to remove'); }
  };

  const updateQty = async (item, delta) => {
    const qty = Math.max(1, item.quantity + delta);
    try {
      await api.put(`/shopping/list/items/${item.id}`, { quantity: qty });
      setList(prev => ({ ...prev, items: prev.items.map(i => i.id === item.id ? { ...i, quantity: qty } : i) }));
    } catch {}
  };

  const crItems = list?.items?.filter(i => i.source === 'connected_rider') || [];
  const amazonItems = list?.items?.filter(i => i.source === 'amazon') || [];
  const manualItems = list?.items?.filter(i => i.source === 'manual' || !i.source) || [];
  const crTotal = crItems.reduce((s, i) => s + (parseFloat(i.price || 0) * i.quantity), 0);
  const amazonTotal = amazonItems.reduce((s, i) => s + (parseFloat(i.price || 0) * i.quantity), 0);

  const ItemRow = ({ item }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--border-color)' }}>
      <button onClick={() => toggleCheck(item)}
        style={{ width: 22, height: 22, borderRadius: 5, border: `1.5px solid ${item.is_checked ? 'var(--cr-green)' : 'var(--border-strong)'}`, background: item.is_checked ? 'var(--cr-green)' : 'none', cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 12 }}>
        {item.is_checked && '✓'}
      </button>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 500, textDecoration: item.is_checked ? 'line-through' : 'none', color: item.is_checked ? 'var(--text-muted)' : 'var(--text-primary)' }}>{item.product_name}</div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 2 }}>
          {item.source === 'connected_rider' && <span className="badge badge-green" style={{ fontSize: 10 }}>Our store</span>}
          {item.source === 'amazon' && <span className="badge badge-amazon" style={{ fontSize: 10 }}>Amazon</span>}
          {item.price && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>${item.price}</span>}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <button onClick={() => updateQty(item, -1)} style={{ width: 22, height: 22, borderRadius: 4, border: '1px solid var(--border-color)', background: 'none', cursor: 'pointer', fontSize: 14 }}>−</button>
        <span style={{ fontSize: 13, fontWeight: 500, minWidth: 16, textAlign: 'center' }}>{item.quantity}</span>
        <button onClick={() => updateQty(item, 1)} style={{ width: 22, height: 22, borderRadius: 4, border: '1px solid var(--border-color)', background: 'none', cursor: 'pointer', fontSize: 14 }}>+</button>
      </div>
      <button onClick={() => removeItem(item.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--text-muted)', padding: '0 2px' }}>×</button>
    </div>
  );

  return (
    <div>
      <TopBar title="Shop & list" subtitle="The Connected Rider · Amazon" />

      <div className="tab-bar">
        <div className={`tab-item ${tab === 'list' ? 'active' : ''}`} onClick={() => setTab('list')}>
          🛒 My list {list?.items?.length > 0 && `(${list.items.length})`}
        </div>
        <div className={`tab-item ${tab === 'search' ? 'active' : ''}`} onClick={() => setTab('search')}>🔍 Search</div>
        <div className={`tab-item ${tab === 'store' ? 'active' : ''}`} onClick={() => setTab('store')}>🏪 Store</div>
      </div>

      {/* SEARCH TAB */}
      {tab === 'search' && (
        <div className="page-content">
          <div style={{ position: 'relative', marginBottom: 14 }}>
            <input className="form-input" placeholder="Search tack, supplies, medications..." value={searchQ} onChange={e => handleSearchInput(e.target.value)} style={{ paddingRight: 36 }} autoFocus />
            {searchQ && <button onClick={() => { setSearchQ(''); setSearchResults(null); }} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--text-muted)' }}>×</button>}
          </div>

          {!searchQ && (
            <>
              <div className="section-label">Quick add</div>
              <div className="chip-group" style={{ marginBottom: 14 }}>
                {QUICK_ITEMS.map(item => (
                  <div key={item} className="chip" onClick={() => handleSearchInput(item)}>{item}</div>
                ))}
              </div>
            </>
          )}

          {searching && <div style={{ textAlign: 'center', padding: 20 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>}

          {searchResults && !searching && (
            <>
              {searchResults.cr_results?.length > 0 && (
                <>
                  <div className="section-label">From The Connected Rider</div>
                  {searchResults.cr_results.map(p => (
                    <div key={p.id} className="card" style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '10px 14px', marginBottom: 8 }}>
                      {p.image && <img src={p.image} alt={p.name} style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{p.name}</div>
                        <div style={{ fontSize: 12, color: 'var(--cr-green)', marginTop: 2 }}>
                          {p.sale_price ? <><span style={{ textDecoration: 'line-through', color: 'var(--text-muted)' }}>${p.regular_price}</span> ${p.sale_price}</> : `$${p.price}`}
                        </div>
                        <span className="badge badge-green" style={{ fontSize: 10, marginTop: 2 }}>Our store</span>
                      </div>
                      <button className="btn btn-primary btn-sm" onClick={() => addToList({ product_name: p.name, source: 'connected_rider', wc_product_id: p.id, price: p.sale_price || p.price, product_url: p.permalink, quantity: 1 })}>Add</button>
                    </div>
                  ))}
                </>
              )}

              {searchResults.supply_results?.length > 0 && (
                <>
                  <div className="section-label">From the supplies catalog</div>
                  {searchResults.supply_results.map(p => (
                    <div key={p.id || p.name} className="card" style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '10px 14px', marginBottom: 8 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{p.name}</div>
                        {p.subcategory && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{p.subcategory}</div>}
                      </div>
                      <button className="btn btn-primary btn-sm" onClick={() => addToList({ product_name: p.name, source: 'manual', quantity: 1 })}>Add</button>
                    </div>
                  ))}
                </>
              )}

              {searchResults.cr_results?.length === 0 && !(searchResults.supply_results?.length > 0) && (
                <div style={{ background: 'var(--bg-secondary)', borderRadius: 'var(--radius-lg)', padding: '16px', textAlign: 'center', marginBottom: 12 }}>
                  <div style={{ fontSize: 24, marginBottom: 8 }}>🔍</div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>We don't carry "{searchQ}" yet</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>We've noted your search — if enough riders want it we'll stock it</div>
                </div>
              )}

              <div className="section-label">Don't see what you need?</div>
              <div className="card" style={{ padding: '8px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--border-color)', cursor: 'pointer' }}
                  onClick={() => addToList({ product_name: searchQ, source: 'amazon', quantity: 1 })}>
                  <div style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--cr-amazon-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ fontSize: 18 }}>📦</span>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>Add "{searchQ}" — link to Amazon</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>We earn a small referral fee · no extra cost to you</div>
                  </div>
                  <span style={{ fontSize: 20, color: 'var(--cr-green)' }}>+</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', cursor: 'pointer' }}
                  onClick={() => addToList({ product_name: searchQ, source: 'manual', quantity: 1 })}>
                  <div style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ fontSize: 18 }}>📝</span>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>Add "{searchQ}" as a note</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Just a reminder to get it</div>
                  </div>
                  <span style={{ fontSize: 20, color: 'var(--cr-green)' }}>+</span>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* LIST TAB */}
      {tab === 'list' && (
        <div className="page-content">
          {loading && <div className="loading-screen"><div className="spinner" /></div>}
          {!loading && (
            <>
              <button className="btn btn-primary btn-full" onClick={() => setTab('search')} style={{ marginBottom: 12 }}>
                🔍 Search & add items
              </button>

              {list?.items?.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-icon">🛒</div>
                  <h3>Your list is empty</h3>
                  <p>Search for products or tap quick add to build your list</p>
                </div>
              ) : (
                <>
                  {crItems.length > 0 && (
                    <>
                      <div className="section-label">In our store — ready to order</div>
                      <div className="card" style={{ padding: '0 14px' }}>
                        {crItems.map(item => <ItemRow key={item.id} item={item} />)}
                      </div>
                    </>
                  )}
                  {amazonItems.length > 0 && (
                    <>
                      <div className="section-label">Not in our store — links to Amazon</div>
                      <div className="card" style={{ padding: '0 14px' }}>
                        {amazonItems.map(item => <ItemRow key={item.id} item={item} />)}
                      </div>
                    </>
                  )}
                  {manualItems.length > 0 && (
                    <>
                      <div className="section-label">Other items</div>
                      <div className="card" style={{ padding: '0 14px' }}>
                        {manualItems.map(item => <ItemRow key={item.id} item={item} />)}
                      </div>
                    </>
                  )}

                  {(crItems.length > 0 || amazonItems.length > 0) && (
                    <div className="card" style={{ background: 'var(--bg-secondary)' }}>
                      {crItems.length > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 13 }}>
                          <span style={{ color: 'var(--text-secondary)' }}>Connected Rider subtotal</span>
                          <span style={{ fontWeight: 600, color: 'var(--cr-green)' }}>${crTotal.toFixed(2)}</span>
                        </div>
                      )}
                      {amazonItems.length > 0 && amazonTotal > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 13, borderTop: crItems.length > 0 ? '1px solid var(--border-color)' : 'none' }}>
                          <span style={{ color: 'var(--text-secondary)' }}>Amazon est.</span>
                          <span style={{ fontWeight: 600, color: '#856404' }}>~${amazonTotal.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {crItems.length > 0 && (
                    <a href="https://theconnectedrider.com/cart" target="_blank" rel="noreferrer" className="btn btn-primary btn-full" style={{ display: 'flex', textDecoration: 'none', marginBottom: 8 }}>
                      🛒 Order from Connected Rider{crTotal > 0 ? ` — $${crTotal.toFixed(2)}` : ''}
                    </a>
                  )}
                  {amazonItems.length > 0 && (
                    <button className="btn btn-amazon btn-full" onClick={() => toast('Opening Amazon items...')}>
                      📦 Get Amazon items{amazonTotal > 0 ? ` — ~$${amazonTotal.toFixed(2)}` : ''}
                    </button>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* STORE TAB */}
      {tab === 'store' && (
        <div className="page-content">
          <div className="card" style={{ textAlign: 'center', padding: 24 }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🏪</div>
            <h3>The Connected Rider</h3>
            <p style={{ margin: '8px 0 16px' }}>Browse our full selection of tack, apparel, saddles, and supplies</p>
            <a href="https://theconnectedrider.com" target="_blank" rel="noreferrer" className="btn btn-primary" style={{ textDecoration: 'none', display: 'inline-flex' }}>
              Browse store →
            </a>
          </div>
          <div className="section-label">Shop by category</div>
          {[['English tack','/english-horse-tack/'],['Western tack','/western-horse-tack/'],['Apparel','/equestrian-apparel/'],['Fly protection','/fly-protection/'],['New arrivals','/newhorsetack/'],['Sale','/englishhorsetackclearancesale/']].map(([label, path]) => (
            <a key={path} href={`https://theconnectedrider.com${path}`} target="_blank" rel="noreferrer"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'white', borderBottom: '1px solid var(--border-color)', textDecoration: 'none', color: 'var(--text-primary)', fontSize: 14 }}>
              {label} <span style={{ color: 'var(--text-muted)' }}>›</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

export default ShoppingPage;
